import React, { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SendHorizontal, User, Mic, Keyboard } from 'lucide-react';
import { TypingIndicator } from '@/components/typing-indicator';
import { DEAIChatMessage, type MessageType, type MessageData } from '@/components/deai-chatbox-message';
import { MicButton } from '@/components/deai-mic-button';
import { AudioRecorderProvider } from '@/hooks/audio-recorder-context';
import { ScrollAreaAuto } from '@/components/ui/scroll-area-auto';
import { DEAIChatHeader, type RestaurantInfo } from '@/components/deai-chat-header';
import { useI18n } from '@/hooks/useI18n';


type ChatMessage = {
  role: string;
  content: string;
  partial?: boolean;
  id?: string;
  messageType?: MessageType;
  messageData?: MessageData;
  turnId?: string;
};

type AgentThinkingState = {
  type: string;
  status: string;
  toolName?: string;
};

interface DEAIChatBoxProps {
  className?: string;
  chatMessages?: ChatMessage[];
  isTyping?: boolean;
  agentThinking?: AgentThinkingState | null;
  onAudioAvailable?: (blob: Blob) => void;
  onSendMessage?: (message: any) => void;
  onTextInputChange?: (text: string) => void;
  restaurantInfo?: RestaurantInfo;
  onRestartSession?: () => void;
  isSessionEnded?: boolean;
  isTTSMuted?: boolean;
  onTTSMuteToggle?: () => void;
}

export const DEAIChatBox = ({
  className,
  chatMessages = [],
  isTyping = false,
  agentThinking = null,
  onAudioAvailable,
  onSendMessage,
  onTextInputChange,
  restaurantInfo,
  onRestartSession,
  isSessionEnded = false,
  isTTSMuted = false,
  onTTSMuteToggle
}: DEAIChatBoxProps) => {
  const { t } = useI18n();
  // State Management - only for the input field and local state
  const [messageInput, setMessageInput] = useState("");
  const [inputMode, setInputMode] = useState<'text' | 'voice'>('text');
  
  // Default className to make the component fit parent with proper overflow containment
  const defaultClassName = "w-full h-full flex flex-col overflow-hidden";
  const combinedClassName = className 
    ? `${defaultClassName} ${className}`
    : defaultClassName;
  
  // Send user message
  const sendUserMessage = useCallback(() => {
    const content = messageInput.trim();
    if (!content) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      action: "message",
      role: "user",
      content,
    };
    
    // Call the callback for sending message
    if (onSendMessage) {
      onSendMessage(userMessage);
    }
    
    // Clear the input after sending
    setMessageInput("");
  }, [messageInput, onSendMessage]);

  // Handle audio available from mic button
  const handleAudioAvailable = useCallback((blob: Blob) => {
    // Process the recorded audio blob
    console.log("Processing audio blob:", blob);
    if (onAudioAvailable) {
      onAudioAvailable(blob);
    }
  }, [onAudioAvailable]);

  // Handle mic button errors
  const handleMicError = useCallback((message: string) => {
    console.error("Mic error:", message);
  }, []);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setMessageInput(newValue);
    if (onTextInputChange) {
      onTextInputChange(newValue);
    }
  };

  // Handle input key down
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendUserMessage();
    }
  };

  // Handle interactive message actions (button clicks, etc.)
  const handleActionClick = useCallback((action: any) => {
    console.log('Action clicked:', action);

    // Send the action value as a message
    if (action.value) {
      const actionMessage = {
        id: `action_${Date.now()}`,
        action: "message",
        role: "user",
        content: action.value,
      };

      if (onSendMessage) {
        onSendMessage(actionMessage);
      }
    }
  }, [onSendMessage]);

  // Handle confirm actions
  const handleConfirm = useCallback((value?: string) => {
    console.log('Confirm action:', value);

    const confirmMessage = {
      id: `confirm_${Date.now()}`,
      action: "message",
      role: "user",
      content: value || "Yes",
    };

    if (onSendMessage) {
      onSendMessage(confirmMessage);
    }
  }, [onSendMessage]);

  // Handle cancel actions
  const handleCancel = useCallback((value?: string) => {
    console.log('Cancel action:', value);

    const cancelMessage = {
      id: `cancel_${Date.now()}`,
      action: "message",
      role: "user",
      content: value || "No",
    };

    if (onSendMessage) {
      onSendMessage(cancelMessage);
    }
  }, [onSendMessage]);

  return (
    <AudioRecorderProvider>
      <div className={combinedClassName}>
        {/* Chat Header */}
        <DEAIChatHeader
          restaurantInfo={restaurantInfo}
          isTTSMuted={isTTSMuted}
          onTTSMuteToggle={onTTSMuteToggle}
        />

        {/* Messages Area */}
        <div className="flex-1 mb-4 overflow-hidden">
          <ScrollAreaAuto autoScrollToBottom className="h-full">
            <div className="flex flex-col gap-3 pr-4 px-2">
              {chatMessages.map((item, index) => (
                <DEAIChatMessage
                  key={item.id || index}
                  variant={item.role === "user" ? "user" : "assistant"}
                  messageType={item.messageType || 'text'}
                  messageData={item.messageData}
                  onActionClick={handleActionClick}
                  onConfirm={handleConfirm}
                  onCancel={handleCancel}
                  onRestartSession={onRestartSession}
                >
                  {item.content}
                </DEAIChatMessage>
              ))}
              {agentThinking && (
                <DEAIChatMessage variant="assistant">
                  <div className="flex items-center gap-2 animate-pulse">
                    <span className="text-lg">⚙️</span>
                    <span className="text-sm">{agentThinking.status}</span>
                  </div>
                </DEAIChatMessage>
              )}
              {isTyping && <TypingIndicator isTyping={isTyping} />}
            </div>
          </ScrollAreaAuto>
        </div>

        {/* Input Area - Hidden when session is ended */}
        {!isSessionEnded && (
          <div className="shrink-0 flex flex-col gap-3 px-2 pb-2">
            <div className="flex gap-2 items-center">
              {/* User Profile Icon */}
              <button
                className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[var(--icon-user-from)] to-[var(--icon-user-to)] border-2 border-white/80 shadow-lg shadow-[color:var(--icon-user-shadow)] transition-all duration-200 hover:scale-105 flex items-center justify-center"
                aria-label={t('chat.userProfileAriaLabel')}
              >
                <User className="size-6 text-white" />
              </button>

              {inputMode === 'text' ? (
                <>
                  {/* Text Input Mode */}
                  <div className="relative flex-grow">
                    <Input
                      className='h-12 w-full rounded-[3em] pl-5 pr-12 text-sm md:text-base text-[color:var(--input-text)] placeholder:text-gray-400 outline-none ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-[color:var(--input-bg)] backdrop-blur-sm border-2 border-[color:var(--input-border)] shadow-lg shadow-[color:var(--input-shadow)] transition-all duration-200 hover:shadow-cyan-200/40 focus:shadow-cyan-300/50'
                      type="text"
                      placeholder={t('chat.inputPlaceholder')}
                      autoComplete="off"
                      value={messageInput}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                    {/* Mic Icon Toggle Button - Inside Input */}
                    <button
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-gray-100 transition-colors"
                      onClick={() => setInputMode('voice')}
                      aria-label="Switch to voice input"
                    >
                      <Mic className="size-5 text-gray-500" />
                    </button>
                  </div>

                  {/* Send Button */}
                  <Button
                    className="shrink-0 rounded-full w-12 h-12 bg-gradient-to-br from-[var(--btn-primary-from)] to-[var(--btn-primary-to)] border-2 border-white/60 shadow-lg shadow-[color:var(--btn-primary-shadow)] transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    onClick={sendUserMessage}
                    disabled={messageInput.trim() === ""}
                    aria-label={t('chat.sendAriaLabel')}
                  >
                    <SendHorizontal className="size-5 text-white" />
                  </Button>
                </>
              ) : (
                <>
                  {/* Voice Input Mode */}
                  <div className="flex-grow flex items-center justify-center">
                    <MicButton
                      label={t('micButton.label')}
                      onRecordStarted={() => console.log('Recording started')}
                      onRecordEnded={() => console.log('Recording ended')}
                      onAudioAvailable={handleAudioAvailable}
                      onErrorMessage={handleMicError}
                    />
                  </div>

                  {/* Keyboard Icon Toggle Button */}
                  <button
                    className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 hover:from-gray-400 hover:to-gray-500 border-2 border-white/60 shadow-lg shadow-gray-200/40 transition-all duration-200 hover:scale-105 flex items-center justify-center"
                    onClick={() => setInputMode('text')}
                    aria-label="Switch to text input"
                  >
                    <Keyboard className="size-5 text-white" />
                  </button>
                </>
              )}
            </div>

            <p className="text-center text-xs text-gray-400">
              {t('chat.disclaimer')}
            </p>
          </div>
        )}
      </div>
    </AudioRecorderProvider>
  );
};
